import { Helmet } from "react-helmet-async";

import { helmetData } from "@/client/constants/helmet";

export const TermsPage = () => (
  <>
    <Helmet prioritizeSeoTags helmetData={helmetData}>
      <title>Terms of Service - CareerBldr</title>
      <meta
        name="description"
        content="Terms of Service for CareerBldr, the AI-powered resume builder operated by Studio Algorithm."
      />
    </Helmet>

    <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
      <header className="mb-10">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Terms of Service</h1>
        <p className="mt-2 text-xs text-foreground/50">Effective Date: November 1, 2025</p>
      </header>

      <div className="space-y-8">
        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">
            1. Acceptance of Terms
          </h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            By accessing or using CareerBldr (&quot;the Service&quot;), operated by Studio
            Algorithm (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), you agree to be bound
            by these Terms of Service. If you do not agree to these terms, please do not use the
            Service. We reserve the right to update these terms at any time, and your continued use
            constitutes acceptance of any changes.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">
            2. Description of Service
          </h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            CareerBldr is an AI-powered resume building platform that provides tools for creating,
            editing, and managing professional resumes. The Service includes AI-assisted writing
            suggestions, resume templates, formatting tools, and related career development features.
            Features may be added, modified, or removed at our discretion.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">3. User Accounts</h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            To access certain features, you must create an account. You are responsible for
            maintaining the confidentiality of your account credentials and for all activities that
            occur under your account. You agree to provide accurate and complete information during
            registration and to update your information as necessary. You must notify us immediately
            of any unauthorized use of your account.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">4. User Content</h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            You retain ownership of all content you create using the Service, including resumes,
            cover letters, and other documents (&quot;User Content&quot;). By using the Service, you
            grant us a limited, non-exclusive license to process and store your User Content solely
            for the purpose of providing and improving the Service. We do not claim ownership of your
            User Content and will not use it for purposes unrelated to the Service without your
            consent.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">
            5. Intellectual Property
          </h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            The Service, including its design, features, templates, AI models, code, and
            documentation, is owned by Studio Algorithm and protected by intellectual property laws.
            You may not copy, modify, distribute, sell, or lease any part of the Service, nor may you
            reverse-engineer or attempt to extract the source code unless applicable law expressly
            permits it.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">6. Privacy</h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            Your use of the Service is also governed by our{" "}
            <a
              href="/privacy"
              className="font-medium text-primary underline decoration-primary/30 underline-offset-2 transition-colors hover:text-primary/80"
            >
              Privacy Policy
            </a>
            , which describes how we collect, use, and protect your personal information. By using
            the Service, you consent to the practices described in the Privacy Policy.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">7. Payment Terms</h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            Certain features of the Service require a paid subscription. Prices are displayed at the
            time of purchase and may change with notice. New subscriptions include a 3-day free
            trial; a payment method is required at checkout, and the subscription renews unless you
            cancel before the trial ends. Subscriptions automatically renew unless cancelled before
            the renewal date. Refunds are handled in accordance with our refund policy. You are
            responsible for any applicable taxes associated with your subscription. If you created a
            resume before paid plans returned, you may continue to download and export that existing
            content without a subscription.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">8. Disclaimers</h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            The Service is provided &quot;as is&quot; and &quot;as available&quot; without warranties
            of any kind, whether express or implied, including but not limited to implied warranties
            of merchantability, fitness for a particular purpose, and non-infringement. We do not
            guarantee that AI-generated suggestions will be accurate, complete, or suitable for your
            specific needs. You are responsible for reviewing and editing all content before use.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">
            9. Limitation of Liability
          </h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            To the maximum extent permitted by applicable law, Studio Algorithm and its affiliates,
            officers, employees, and agents shall not be liable for any indirect, incidental, special,
            consequential, or punitive damages, or any loss of profits or revenue, whether incurred
            directly or indirectly, or any loss of data, use, goodwill, or other intangible losses
            resulting from your use of or inability to use the Service.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">10. Termination</h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            We may suspend or terminate your access to the Service at any time, with or without
            cause, and with or without notice. Upon termination, your right to use the Service ceases
            immediately. You may export your User Content before termination. Provisions of these
            terms that by their nature should survive termination shall remain in effect.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">
            11. Changes to Terms
          </h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            We reserve the right to modify these Terms of Service at any time. Material changes will
            be communicated via email or through a notice on the Service. Your continued use of the
            Service after changes are posted constitutes your acceptance of the revised terms. We
            encourage you to review these terms periodically.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">12. Contact</h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            If you have questions about these Terms of Service, please contact us at{" "}
            <a
              href="mailto:support@careerbldr.com"
              className="font-medium text-primary underline decoration-primary/30 underline-offset-2 transition-colors hover:text-primary/80"
            >
              support@careerbldr.com
            </a>
            .
          </p>
          <p className="mt-4 text-sm leading-relaxed text-foreground/70">
            CareerBldr is operated by Studio Algorithm.
          </p>
        </section>
      </div>
    </div>
  </>
);
